import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { protectedResources } from '../../../config/msalConfig';

const API_BASE = protectedResources.api.baseEndPoint;

/**
 * useConversations Hook
 * Manages conversation history, CRUD operations, and message loading
 */
export const useConversations = () => {
  const { instance, accounts } = useMsal();
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loading, setLoading] = useState(false);

  const user = accounts[0];
  const userId = user?.localAccountId || user?.idTokenClaims?.oid;
  const organizationId = user?.idTokenClaims?.extension_OrganizationId || 1;

  const getAuthToken = async () => {
    const request = {
      scopes: protectedResources.api.scopes.read,
      account: accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  };

  const getHeaders = async () => {
    const token = await getAuthToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  /**
   * Load all conversations for the current user
   */
  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getHeaders();
      const response = await axios.get(
        `${API_BASE}/v1/copilot/conversations`,
        { headers }
      );

      if (response.data?.success) {
        setConversations(response.data.data || []);

        // Set most recent as active if none selected
        if (!activeConversationId && response.data.data.length > 0) {
          setActiveConversationId(response.data.data[0].conversationId);
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [userId, organizationId, activeConversationId]);

  /**
   * Create a new conversation
   */
  const createConversation = useCallback(
    async (title) => {
      try {
        const headers = await getHeaders();
        const response = await axios.post(
          `${API_BASE}/v1/copilot/conversations`,
          { title },
          { headers }
        );

        if (response.data?.success) {
          const newConversation = response.data.data;
          setConversations((prev) => [newConversation, ...prev]);
          setActiveConversationId(newConversation.conversationId);
          return newConversation;
        }
      } catch (error) {
        console.error('Failed to create conversation:', error);
        throw error;
      }
    },
    [userId, organizationId]
  );

  /**
   * Update an existing conversation
   */
  const updateConversation = useCallback(
    async (conversationId, updates) => {
      try {
        const headers = await getHeaders();
        const response = await axios.put(
          `${API_BASE}/v1/copilot/conversations/${conversationId}`,
          updates,
          { headers }
        );

        if (response.data?.success) {
          setConversations((prev) =>
            prev.map((conv) =>
              conv.conversationId === conversationId
                ? { ...conv, ...updates }
                : conv
            )
          );
        }
      } catch (error) {
        console.error('Failed to update conversation:', error);
        throw error;
      }
    },
    []
  );

  /**
   * Delete a conversation
   */
  const deleteConversation = useCallback(
    async (conversationId) => {
      try {
        const headers = await getHeaders();
        await axios.delete(`${API_BASE}/v1/copilot/conversations/${conversationId}`, {
          headers,
        });

        setConversations((prev) =>
          prev.filter((conv) => conv.conversationId !== conversationId)
        );

        if (activeConversationId === conversationId) {
          setActiveConversationId(null);
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        throw error;
      }
    },
    [activeConversationId]
  );

  /**
   * Load messages for a specific conversation
   */
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const headers = await getHeaders();
      const response = await axios.get(
        `${API_BASE}/v1/copilot/conversations/${conversationId}/messages`,
        { headers }
      );

      if (response.data?.success) {
        return response.data.data || [];
      }

      return [];
    } catch (error) {
      console.error('Failed to load messages:', error);
      return [];
    }
  }, []);

  /**
   * Select a conversation as active
   */
  const selectConversation = useCallback((conversationId) => {
    setActiveConversationId(conversationId);
  }, []);

  return {
    conversations,
    activeConversationId,
    loading,
    loadConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    loadMessages,
    selectConversation,
  };
};
